/**
 * Shared utilities for the tour component and its sub-components.
 */

/**
 * Checks whether an element is visible by walking up its ancestor chain.
 * Returns false if any ancestor has `display: none` or `visibility: hidden`.
 */
export const isElementVisible = (element: HTMLElement): boolean => {
  let currentElement: HTMLElement | null = element;

  while (currentElement) {
    if (currentElement === document.body) {
      break;
    }

    const { display, visibility } = getComputedStyle(currentElement);
    if (display === 'none' || visibility === 'hidden') {
      return false;
    }

    currentElement = currentElement.parentElement ?? null;
  }

  return true;
};

interface PollUntilOptions {
  maxAttempts?: number;
  delayMs?: number;
}

/**
 * Polls a predicate function until it returns true or the attempt limit is reached.
 * Returns a cleanup function that cancels any pending timeout.
 *
 * @param predicate - Function that returns true when the condition is met.
 * @param onReady - Called when the predicate returns true.
 * @param onTimeout - Called when maxAttempts is exhausted without success.
 * @param options - Polling configuration.
 * @returns A cleanup function to cancel pending polling.
 */
export const pollUntil = (
  predicate: () => boolean,
  onReady: () => void,
  onTimeout: () => void,
  { maxAttempts = 200, delayMs = 100 }: PollUntilOptions = {}
): (() => void) => {
  let attempts = 0;
  let timeoutId: number | null = null;

  const check = () => {
    if (predicate()) {
      onReady();
      return;
    }

    if (attempts >= maxAttempts) {
      onTimeout();
      return;
    }

    attempts += 1;
    timeoutId = window.setTimeout(check, delayMs);
  };

  check();

  return () => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
};
